import Button from '@/features/shared/Button';
import Image from '@/features/shared/Image';
import { MuiTypography } from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import TwoColumnContent from '@/features/shared/TwoColumnContent';
import { getPageUrl } from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import clsx from 'clsx';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Fade } from 'react-reveal';
import { NewsProps } from './News.interface';
import useStyles from './News.styles';

const NewsComponent = ({
  introContent,
  featuredContent,
  curatedList,
  rightRailContent = [],
  theme,
  contentButtons,
  topSpacing,
  isSticky,
}: Br.PropsWithContentButtons<NewsProps>) => {
  const page = React.useContext(BrPageContext);
  const classes = useStyles({});
  const dynamicList = useMemo(
    () => rightRailContent?.slice(curatedList?.contentList?.length || 0) || [],
    [curatedList?.contentList?.length, rightRailContent],
  );

  const sortedDynamicList = useMemo(
    () => dynamicList.sort((a, b) => Date.parse(b.publicationDate) - Date.parse(a.publicationDate)),
    [dynamicList],
  );
  const { contentList = [] } = curatedList || {};
  const newsList = useMemo(
    () => [...contentList, ...sortedDynamicList],
    [contentList, sortedDynamicList],
  );
  const [height, setHeight] = useState<any>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  useEffect(() => {
    if (introRef?.current) {
      setHeight(isMobile ? 0 : `${introRef?.current?.clientHeight + 16}px`);
    }
  }, [isMobile]);

  const rootClassName = clsx(
    classes.root,
    `${theme}-theme`,
    `top-${topSpacing}`,
    isSticky && 'is-sticky',
  );

  const getUrl = React.useCallback(
    ({ cardLink, url }: { cardLink?: string; url: string }) => {
      const cardUrl = cardLink || url;
      return page && getPageUrl({ url: cardUrl, page });
    },
    [page],
  );

  return (
    <section data-testid="qa-news-component" className={rootClassName}>
      <TwoColumnContent
        columnRatio="70-30"
        leftColumn={
          <div className={classes.leftContent}>
            <div className={classes.intro} ref={introRef}>
              {contentButtons?.introContent}
              <Fade>
                <>
                  {introContent?.heading && (
                    <MuiTypography variant="h2" component="h2" className={classes.heading}>
                      {introContent?.heading}
                    </MuiTypography>
                  )}
                  {introContent?.detailContent && (
                    <RichText
                      theme={theme}
                      content={introContent?.detailContent}
                      className={classes.heading}
                    />
                  )}
                  {/* <p className={classes.subHeading}>{introContent?.subHeading}</p> */}
                </>
              </Fade>
            </div>
            <article className={classes.featured}>
              {contentButtons?.featuredContent}
              <Fade>
                <>
                  {featuredContent?.image && (
                    <Image
                      image={{ ...featuredContent?.image, fullWidthImage: true }}
                      className={classes.image}
                    />
                  )}
                  {(featuredContent?.publicationDate ||
                    featuredContent?.cardCategory ||
                    featuredContent?.author?.name) && (
                    <MuiTypography variant="caption" component="p" className={classes.info}>
                      {featuredContent?.publicationDate && (
                        <span className={classes.date}>{featuredContent?.publicationDate}</span>
                      )}
                      {featuredContent?.cardCategory && (
                        <span className={classes.date}>{featuredContent?.cardCategory?.label}</span>
                      )}
                      {featuredContent?.author?.name && (
                        <span className={classes.author}>{featuredContent?.author?.name}</span>
                      )}
                    </MuiTypography>
                  )}
                  <div>
                    {featuredContent?.heading && (
                      <Button
                        variant="link"
                        theme={theme}
                        link={{ href: getUrl(featuredContent) }}
                        endIcon={<ChevronRightIcon className={classes.icon} />}
                        className={clsx(classes.link, classes.featureLink, 'gtm-featured-item')}
                      >
                        <MuiTypography variant="h5" component="h5">
                          {featuredContent?.heading}
                        </MuiTypography>
                      </Button>
                    )}
                    {featuredContent?.cardDescription && (
                      <MuiTypography
                        variant="body2"
                        component="div"
                        dangerouslySetInnerHTML={{ __html: featuredContent?.cardDescription }}
                      />
                    )}
                  </div>
                </>
              </Fade>
            </article>
          </div>
        }
        rightColumn={
          <div className={classes.list}>
            {contentButtons?.curatedList}
            <div style={{ height }} />
            {newsList
              .filter((item: any) => item?.heading || item?.cardHeading)
              .map((item: any) => ({
                ...item,
                cardPublicationDate: item?.cardPublicationDate || item?.releaseDate,
              }))
              .map((item: any, idx: number) => (
                <Fade key={`news_${idx}`}>
                  <article>
                    <div className={classes.listItem}>
                      {(item?.cardPublicationDate || item?.cardCategory) && (
                        <MuiTypography variant="caption" component="p" className={classes.info}>
                          {item?.cardPublicationDate && (
                            <span className={classes.date}>{item?.cardPublicationDate}</span>
                          )}
                          {item?.cardCategory && (
                            <span className={classes.category}>{item?.cardCategory?.label}</span>
                          )}
                        </MuiTypography>
                      )}
                      {item.cardHeading && (
                        <Button
                          variant="link"
                          link={{ href: getUrl(item) }}
                          endIcon={<ChevronRightIcon className={classes.icon} />}
                          className={clsx(classes.link, 'gtm-list-item')}
                        >
                          <MuiTypography variant="body1" component="span">
                            {item?.cardHeading}
                          </MuiTypography>
                        </Button>
                      )}
                    </div>
                  </article>
                </Fade>
              ))}
          </div>
        }
      />
    </section>
  );
};

export default memo(NewsComponent);
